'use client';

import { useCallback, useId, useMemo, useState } from 'react';

import { COMPONENT_IDS } from '@/hen-ui/catalog/component-ids';
import { Heading } from '@/hen-ui/atomics/Heading';
import { Label } from '@/hen-ui/atomics/Label';
import { Text } from '@/hen-ui/atomics/Text';
import { Select, SelectItem } from '@/hen-ui/atomics/Select';
import { CareerExplorerShell } from '@/hen-ui/composites/CareerExplorer/CareerExplorerShell';
import { Flex } from '@/hen-ui/composites/Flex';

import { CareerExplorerChipGroup } from './CareerExplorerChipGroup';
import type { CareerExplorerSelectorOption } from './types';

export type { CareerExplorerSelectorOption };

export interface CareerExplorerSelection {
  careerId: string | null;
  goalChip: string | null;
  levelChip: string | null;
}

export interface CareerExplorerSelectorProps {
  /** Question shown above the career dropdown. Omitted when empty. */
  heading?: string;
  /** Note shown beside the question. Omitted when empty. */
  questionNote?: string;
  /** Careers listed in the dropdown. */
  careerOptions: CareerExplorerSelectorOption[];
  /** Single-select goal chips (e.g. "Just exploring", "Upskilling"). */
  goalOptions: CareerExplorerSelectorOption[];
  /** Heading above the goal chips. */
  goalHeading?: string;
  /** Single-select experience-level chips (e.g. "Beginner", "Advanced"). */
  levelOptions: CareerExplorerSelectorOption[];
  /** Heading above the experience-level chips. */
  levelHeading?: string;
  /**
   * Empty-state and clear option label for the career dropdown. A blank value
   * falls back to "Clear selection".
   */
  placeholder?: string;
  /** Fires with the full selection whenever the career, goal chip, or level chip changes. */
  onSelectionChange?: (selection: CareerExplorerSelection) => void;
  /**
   * Career the parent accepted. When passed, including `null`, the dropdown
   * and chips follow this id instead of the last value the visitor chose.
   */
  controlledCareerId?: string | null;
}

/**
 * Sentinel dropdown value that resets the career selection back to `null`.
 * Radix's underlying `<Select.Item>` cannot carry an empty-string value, so a
 * dedicated non-empty sentinel is used to represent "no career selected".
 */
const CLEAR_CAREER_VALUE = '__career-explorer-selector-clear__';

/** Accessible name used when the visible career question is omitted. */
const FALLBACK_CAREER_LABEL = 'Career';

const DEFAULT_GOAL_HEADING = 'Choose your goal';
const DEFAULT_LEVEL_HEADING = 'Choose your experience level';
const DEFAULT_PLACEHOLDER = 'Clear selection';

const authoredHeading = (
  value: string | undefined,
  fallback: string,
): string => {
  const trimmed = value?.trim() ?? '';
  return trimmed || fallback;
};

/**
 * CareerExplorerSelector — a career dropdown paired with optional
 * single-select goal and experience-level chip rows. Presentational only: it
 * emits the current selection via `onSelectionChange` and never fetches data
 * or renders a results/peek panel. Goal and level chips stay hidden until a
 * career is selected, and start unselected (`null`). Compact vs expanded
 * layout (one product, then peek) belongs to a parent, not this composite.
 */
export const CareerExplorerSelector = ({
  heading,
  questionNote,
  careerOptions,
  goalOptions,
  goalHeading,
  levelOptions,
  levelHeading,
  placeholder,
  onSelectionChange,
  controlledCareerId,
}: CareerExplorerSelectorProps) => {
  const careerSelectId = useId();
  const visibleQuestion = heading?.trim() ?? '';
  const visiblePlaceholder = authoredHeading(placeholder, DEFAULT_PLACEHOLDER);
  const visibleGoalHeading = authoredHeading(goalHeading, DEFAULT_GOAL_HEADING);
  const visibleLevelHeading = authoredHeading(
    levelHeading,
    DEFAULT_LEVEL_HEADING,
  );
  const [careerId, setCareerId] = useState<string | null>(null);
  const selectedCareerId =
    controlledCareerId !== undefined ? controlledCareerId : careerId;
  const [goalChip, setGoalChip] = useState<string | null>(null);
  const [levelChip, setLevelChip] = useState<string | null>(null);

  const safeCareerOptions = useMemo(
    () => careerOptions.filter((option) => option.id !== CLEAR_CAREER_VALUE),
    [careerOptions],
  );

  const handleCareerChange = useCallback(
    (value: string) => {
      const nextCareerId = value === CLEAR_CAREER_VALUE ? null : value;
      setCareerId(nextCareerId);
      onSelectionChange?.({ careerId: nextCareerId, goalChip, levelChip });
    },
    [goalChip, levelChip, onSelectionChange],
  );

  const handleGoalChange = useCallback(
    (value: string) => {
      const nextGoalChip = value === '' ? null : value;
      setGoalChip(nextGoalChip);
      onSelectionChange?.({
        careerId: selectedCareerId,
        goalChip: nextGoalChip,
        levelChip,
      });
    },
    [levelChip, onSelectionChange, selectedCareerId],
  );

  const handleLevelChange = useCallback(
    (value: string) => {
      const nextLevelChip = value === '' ? null : value;
      setLevelChip(nextLevelChip);
      onSelectionChange?.({
        careerId: selectedCareerId,
        goalChip,
        levelChip: nextLevelChip,
      });
    },
    [goalChip, onSelectionChange, selectedCareerId],
  );

  return (
    <CareerExplorerShell
      data-hen-id={COMPONENT_IDS.CareerExplorerSelector}
      gap='lg'
    >
      {(visibleQuestion || questionNote) && (
        <Flex direction='row' gap='xxs' className='items-baseline'>
          {visibleQuestion ? (
            <Heading variant='h2' tag='h3' className='text-neutral-900'>
              <Label
                htmlFor={careerSelectId}
                className='text-[length:inherit] leading-[inherit] [font-weight:inherit] [color:inherit]'
              >
                {visibleQuestion}
              </Label>
            </Heading>
          ) : null}
          {questionNote && (
            <Text
              tag='span'
              variant='b4'
              className='hidden italic sm:inline text-neutral-900'
            >
              {questionNote}
            </Text>
          )}
        </Flex>
      )}
      {visibleQuestion ? null : (
        <Label htmlFor={careerSelectId} className='sr-only'>
          {FALLBACK_CAREER_LABEL}
        </Label>
      )}

      <Flex direction='column' gap='xs'>
        <Select
          id={careerSelectId}
          native
          variant='elevated'
          placeholder={visiblePlaceholder}
          value={selectedCareerId ?? ''}
          onValueChange={handleCareerChange}
        >
          <SelectItem value={CLEAR_CAREER_VALUE}>
            {visiblePlaceholder}
          </SelectItem>
          {safeCareerOptions.map((option) => (
            <SelectItem key={option.id} value={option.id}>
              {option.label}
            </SelectItem>
          ))}
        </Select>
      </Flex>

      {selectedCareerId && (
        <CareerExplorerChipGroup
          icon='trophy'
          label={visibleGoalHeading}
          options={goalOptions}
          value={goalChip}
          onValueChange={handleGoalChange}
          ariaLabel={visibleGoalHeading}
        />
      )}

      {selectedCareerId && (
        <CareerExplorerChipGroup
          icon='user'
          label={visibleLevelHeading}
          options={levelOptions}
          value={levelChip}
          onValueChange={handleLevelChange}
          ariaLabel={visibleLevelHeading}
        />
      )}
    </CareerExplorerShell>
  );
};
